// src/pages/admin/PaymentRequests.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'react-toastify';
import {
  CreditCard,
  Filter,
  RefreshCw,
  Search,
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  Download,
  DollarSign,
  TrendingUp,
  Users,
  Calendar,
  Building2,
  User,
  Phone,
  Mail,
  MapPin,
  FileText,
  Banknote
} from 'lucide-react';
import PaymentRequestDetailsModal from '../../components/payments/PaymentRequestDetailsModal';
import ApprovalModal from '../../components/payments/ApprovalModal';
import { GET_PAYMENT_REQUESTS, GET_PAYMENT_REQUESTS_COUNT, APPROVE_PAYMENT_REQUEST, REJECT_PAYMENT_REQUEST } from '../../api/payment-requests-api';
import { exportToExcel } from '../../utils/exportToExcel';

const PaymentRequests = () => {
  // State management
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [approvalAction, setApprovalAction] = useState(null); // 'approve' or 'reject'
  const [rejectionReason, setRejectionReason] = useState('');
  const [approvalMessage, setApprovalMessage] = useState('');
  const location = useLocation();
  const locationHandledRef = useRef(false);

  // Filters and pagination
  const [filters, setFilters] = useState({
    status: '',
    userType: '',
    searchTerm: '',
    dateFrom: '',
    dateTo: '',
    minAmount: '',
    maxAmount: ''
  });
  const [showDuplicatesOnly, setShowDuplicatesOnly] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [sortBy, setSortBy] = useState('dateDemande');
  const [sortOrder, setSortOrder] = useState('desc');

  // Push status filter to server — all other filters remain client-side
  const serverVars = { status: filters.status || undefined };

  const {
    loading,
    error,
    data: paymentRequestsData,
    refetch: refetchPaymentRequests
  } = useQuery(GET_PAYMENT_REQUESTS, {
    variables: serverVars,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
  });

  const { data: countData } = useQuery(GET_PAYMENT_REQUESTS_COUNT, {
    variables: serverVars,
    fetchPolicy: 'cache-and-network',
  });

  // Reset to first page when status filter changes
  React.useEffect(() => { setCurrentPage(1); }, [filters.status]);

  // Open details modal when navigated from notifications page with a specific request ID
  React.useEffect(() => {
    const openId = location.state?.openRequestId;
    const list = paymentRequestsData?.paymentRequests;
    if (!openId || locationHandledRef.current || !list?.length) return;
    const req = list.find(r => r.id === openId);
    if (req) {
      locationHandledRef.current = true;
      setSelectedRequest(req);
      setShowDetailsModal(true);
    }
  }, [location.state, paymentRequestsData]);

  const [approvePaymentRequest] = useMutation(APPROVE_PAYMENT_REQUEST, {
    onCompleted: () => {
      toast.success('Demande de paiement approuvée avec succès');
      setShowApprovalModal(false);
      setSelectedRequest(null);
      refetchPaymentRequests();
    },
    onError: (error) => {
      toast.error(`Erreur lors de l'approbation: ${error.message}`);
    }
  });

  const [rejectPaymentRequest] = useMutation(REJECT_PAYMENT_REQUEST, {
    onCompleted: () => {
      toast.success('Demande de paiement rejetée');
      setShowApprovalModal(false);
      setSelectedRequest(null);
      setRejectionReason('');
      refetchPaymentRequests();
    },
    onError: (error) => {
      toast.error(`Erreur lors du rejet: ${error.message}`);
    }
  });

  const paymentRequests = paymentRequestsData?.paymentRequests || [];

  const hasDuplicateTransactions = (request) => {
    const txs = request?.transactions;
    if (!txs || txs.length === 0) return false;
    const codes = txs.filter(t => t.type === 'SCAN' && t.label?.code).map(t => t.label.code);
    return codes.length !== new Set(codes).size;
  };

  // Filter payment requests — status is handled server-side
  const filteredRequests = paymentRequests.filter(request => {
    // Duplicates-only filter
    if (showDuplicatesOnly && !(request.hasDuplicates ?? hasDuplicateTransactions(request))) return false;
    // User type filter
    if (filters.userType && request.user?.userType !== filters.userType) return false;

    // Date range filter
    if (filters.dateFrom) {
      const requestDate = new Date(request.dateDemande);
      const fromDate = new Date(filters.dateFrom);
      if (requestDate < fromDate) return false;
    }

    if (filters.dateTo) {
      const requestDate = new Date(request.dateDemande);
      const toDate = new Date(filters.dateTo);
      toDate.setHours(23, 59, 59, 999);
      if (requestDate > toDate) return false;
    }

    // Amount range filter
    if (filters.minAmount && request.amount < parseFloat(filters.minAmount)) return false;
    if (filters.maxAmount && request.amount > parseFloat(filters.maxAmount)) return false;

    // Search term filter
    if (filters.searchTerm) {
      const term = filters.searchTerm.toLowerCase();
      return (
        request.id.toLowerCase().includes(term) ||
        (request.user?.firstName?.toLowerCase() || '').includes(term) ||
        (request.user?.lastName?.toLowerCase() || '').includes(term) ||
        (request.user?.email?.toLowerCase() || '').includes(term) ||
        (request.user?.tel?.toLowerCase() || '').includes(term) ||
        (request.user?.pharmacy?.namePharmacy?.toLowerCase() || '').includes(term) ||
        (request.user?.ownedLaboratory?.nom?.toLowerCase() || '').includes(term)
      );
    }

    return true;
  });

  // Sort requests
  const sortedRequests = [...filteredRequests].sort((a, b) => {
    let aValue, bValue;

    switch (sortBy) {
      case 'dateDemande':
        aValue = new Date(a.dateDemande);
        bValue = new Date(b.dateDemande);
        break;
      case 'amount':
        aValue = a.amount;
        bValue = b.amount;
        break;
      case 'user':
        aValue = `${a.user?.firstName || ''} ${a.user?.lastName || ''}`.toLowerCase();
        bValue = `${b.user?.firstName || ''} ${b.user?.lastName || ''}`.toLowerCase();
        break;
      case 'status':
        aValue = a.status;
        bValue = b.status;
        break;
      default:
        aValue = a[sortBy];
        bValue = b[sortBy];
    }

    if (sortOrder === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  // Pagination
  const totalPages = Math.ceil(sortedRequests.length / pageSize);
  const paginatedRequests = sortedRequests.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  // Statistics computed from currently-loaded page (accurate for the active status filter)
  const serverTotal = countData?.paymentRequestsCount ?? paymentRequests.length;
  const stats = {
    total: serverTotal,
    pending: filters.status === 'PENDING' ? serverTotal : paymentRequests.filter(r => r.status === 'PENDING').length,
    approved: filters.status === 'VALIDATED' ? serverTotal : paymentRequests.filter(r => r.status === 'VALIDATED').length,
    rejected: filters.status === 'CANCELLED' ? serverTotal : paymentRequests.filter(r => r.status === 'CANCELLED').length,
    totalAmount: paymentRequests.reduce((sum, r) => sum + r.amount, 0),
    pendingAmount: paymentRequests.filter(r => r.status === 'PENDING').reduce((sum, r) => sum + r.amount, 0),
    approvedAmount: paymentRequests.filter(r => r.status === 'VALIDATED').reduce((sum, r) => sum + r.amount, 0),
    thisMonth: paymentRequests.filter(r => {
      const d = new Date(r.dateDemande); const now = new Date();
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    }).length,
  };

  // Options for filters
  const statusOptions = [
    { value: '', label: 'Tous les statuts' },
    { value: 'PENDING', label: 'En attente' },
    { value: 'VALIDATED', label: 'Approuvée' },
    { value: 'CANCELLED', label: 'Rejetée' },
  ];

  const userTypeOptions = [
    { value: '', label: 'Tous les types' },
    { value: 'PHARMACIST', label: 'Pharmacien' },
    { value: 'SELLER', label: 'Vendeur' },
    { value: 'LABORATORY', label: 'Laboratoire' },
  ];

  // Helper functions
  const getStatusStyle = (status) => {
    switch (status) {
      case 'VALIDATED':
        return {
          bgColor: 'bg-green-100',
          textColor: 'text-green-800',
          icon: <CheckCircle size={14} className="mr-1 text-green-600" />
        };
      case 'PENDING':
        return {
          bgColor: 'bg-yellow-100',
          textColor: 'text-yellow-800',
          icon: <Clock size={14} className="mr-1 text-yellow-600" />
        };
      case 'CANCELLED':
        return {
          bgColor: 'bg-red-100',
          textColor: 'text-red-800',
          icon: <XCircle size={14} className="mr-1 text-red-600" />
        };
      default:
        return {
          bgColor: 'bg-gray-100',
          textColor: 'text-gray-800',
          icon: null
        };
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'VALIDATED': return 'Approuvée';
      case 'PENDING': return 'En attente';
      case 'CANCELLED': return 'Rejetée';
      default: return status;
    }
  };

  const getUserTypeLabel = (userType) => {
    switch (userType) {
      case 'PHARMACIST': return 'Pharmacien';
      case 'SELLER': return 'Vendeur';
      case 'LABORATORY': return 'Laboratoire';
      case 'ADMIN': return 'Admin';
      default: return userType;
    }
  };

  // Event handlers
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setCurrentPage(1);
  };

  const handleRefresh = () => {
    refetchPaymentRequests();
    toast.success('Données actualisées');
  };

  const handleViewDetails = (request) => {
    setSelectedRequest(request);
    setShowDetailsModal(true);
  };

  const handleApprove = (request) => {
    setSelectedRequest(request);
    setApprovalAction('approve');
    setShowApprovalModal(true);
  };

  const handleReject = (request) => {
    setSelectedRequest(request);
    setApprovalAction('reject');
    setShowApprovalModal(true);
  };

  const confirmAction = async () => {
    if (!selectedRequest) return;

    try {
      if (approvalAction === 'approve') {
        await approvePaymentRequest({
          variables: {
            id: selectedRequest.id,
            ...(approvalMessage.trim() ? { approvalMessage: approvalMessage.trim() } : {})
          }
        });
        setApprovalMessage('');
      } else if (approvalAction === 'reject') {
        await rejectPaymentRequest({
          variables: {
            id: selectedRequest.id,
            rejectionReason: rejectionReason || 'Aucune raison spécifiée'
          }
        });
      }
    } catch (error) {
      console.error('Error processing payment request:', error);
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const clearFilters = () => {
    setFilters({
      status: '',
      userType: '',
      searchTerm: '',
      dateFrom: '',
      dateTo: '',
      minAmount: '',
      maxAmount: ''
    });
    setShowDuplicatesOnly(false);
    setCurrentPage(1);
  };

  const handleExport = () => {
    const headers = [
      'ID', 'Date de demande', 'Utilisateur', 'Email',
      'Type d\'utilisateur', 'Montant (DH)', 'Statut', 'Raison de rejet', 'Organisation',
    ];
    const rows = sortedRequests.map(request => [
      request.id,
      format(new Date(request.dateDemande), 'dd/MM/yyyy HH:mm'),
      `${request.user?.firstName || ''} ${request.user?.lastName || ''}`.trim(),
      request.user?.email || '',
      getUserTypeLabel(request.user?.userType || ''),
      request.amount.toFixed(2),
      getStatusLabel(request.status),
      request.rejectionReason || '',
      request.user?.pharmacy?.namePharmacy || request.user?.ownedLaboratory?.nom || '',
    ]);
    exportToExcel(headers, rows, `demandes_paiement_${format(new Date(), 'yyyy-MM-dd')}.xls`);
    toast.success('Export terminé');
  };

  if (error) {
    return (
      <div className="space-y-6">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
          <strong className="font-bold">Erreur!</strong>
          <span className="block sm:inline"> Impossible de charger les demandes de paiement: {error.message}</span>
          <button
            onClick={handleRefresh}
            className="mt-3 bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-700 to-green-600 text-white p-4 sm:p-6 rounded-lg shadow-lg">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold">Demandes de Paiement</h1>
            <p className="text-green-100 mt-1 text-sm hidden sm:block">
              Gestion et approbation des demandes de retrait
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="flex items-center px-3 py-2 text-sm bg-white/20 backdrop-blur rounded-md hover:bg-white/30 transition-colors"
            >
              <RefreshCw size={14} className={`mr-1.5 ${loading ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Actualiser</span>
            </button>
            <button
              onClick={handleExport}
              className="flex items-center px-3 py-2 text-sm bg-white/20 backdrop-blur rounded-md hover:bg-white/30 transition-colors"
            >
              <Download size={14} className="mr-1.5" />
              <span className="hidden sm:inline">Exporter</span>
            </button>
          </div>
        </div>
      </div>

      {/* Statistics cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm text-gray-500 mb-1">Total Demandes</p>
              <p className="text-2xl sm:text-3xl font-bold text-gray-900">{stats.total.toLocaleString()}</p>
              <div className="flex items-center mt-2">
                <TrendingUp className="h-4 w-4 text-blue-500 mr-1" />
                <span className="text-sm text-blue-600">{stats.thisMonth} ce mois</span>
              </div>
            </div>
            <div className="p-3 bg-blue-50 rounded-full">
              <CreditCard size={24} className="text-blue-500" />
            </div>
          </div>
        </div>

        <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm text-gray-500 mb-1">En Attente</p>
              <p className="text-2xl sm:text-3xl font-bold text-yellow-600">{stats.pending.toLocaleString()}</p>
              <div className="flex items-center mt-2">
                <DollarSign className="h-4 w-4 text-yellow-500 mr-1" />
                <span className="text-sm text-yellow-600">
                  {stats.pendingAmount.toLocaleString('fr-FR', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })} DH
                </span>
              </div>
            </div>
            <div className="p-3 bg-yellow-50 rounded-full">
              <Clock size={24} className="text-yellow-500" />
            </div>
          </div>
        </div>

        <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm text-gray-500 mb-1">Approuvées</p>
              <p className="text-2xl sm:text-3xl font-bold text-green-600">{stats.approved.toLocaleString()}</p>
              <div className="flex items-center mt-2">
                <Banknote className="h-4 w-4 text-green-500 mr-1" />
                <span className="text-sm text-green-600">
                  {stats.approvedAmount.toLocaleString('fr-FR', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })} DH
                </span>
              </div>
            </div>
            <div className="p-3 bg-green-50 rounded-full">
              <CheckCircle size={24} className="text-green-500" />
            </div>
          </div>
        </div>

        <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm text-gray-500 mb-1">Rejetées</p>
              <p className="text-2xl sm:text-3xl font-bold text-red-600">{stats.rejected.toLocaleString()}</p>
              <div className="flex items-center mt-2">
                <span className="text-sm text-gray-600">
                  {((stats.rejected / Math.max(stats.total, 1)) * 100).toFixed(1)}% du total
                </span>
              </div>
            </div>
            <div className="p-3 bg-red-50 rounded-full">
              <XCircle size={24} className="text-red-500" />
            </div>
          </div>
        </div>
      </div>

      {/* Advanced Filters */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <Filter className="mr-2 h-5 w-5" />
            Filtres avancés
          </h3>
          <button
            onClick={clearFilters}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            Réinitialiser
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Search */}
          <div>
            <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
              Rechercher
            </label>
            <div className="relative">
              <input
                type="text"
                id="search"
                name="searchTerm"
                value={filters.searchTerm}
                onChange={handleFilterChange}
                className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 sm:text-sm"
                placeholder="Nom, email, téléphone..."
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search size={16} className="text-gray-400" />
              </div>
            </div>
          </div>

          {/* Status */}
          <div>
            <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
              Statut
            </label>
            <select
              id="status"
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 sm:text-sm"
            >
              {statusOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* User Type */}
          <div>
            <label htmlFor="userType" className="block text-sm font-medium text-gray-700 mb-1">
              Type d'utilisateur
            </label>
            <select
              id="userType"
              name="userType"
              value={filters.userType}
              onChange={handleFilterChange}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 sm:text-sm"
            >
              {userTypeOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Page Size */}
          <div>
            <label htmlFor="pageSize" className="block text-sm font-medium text-gray-700 mb-1">
              Par page
            </label>
            <select
              id="pageSize"
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 sm:text-sm"
            >
              {[15, 25, 50, 100].map(size => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </div>

          {/* Date From */}
          <div>
            <label htmlFor="dateFrom" className="block text-sm font-medium text-gray-700 mb-1">
              Du
            </label>
            <input
              type="date"
              id="dateFrom"
              name="dateFrom"
              value={filters.dateFrom}
              onChange={handleFilterChange}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 sm:text-sm"
            />
          </div>

          {/* Date To */}
          <div>
            <label htmlFor="dateTo" className="block text-sm font-medium text-gray-700 mb-1">
              Au
            </label>
            <input
              type="date"
              id="dateTo"
              name="dateTo"
              value={filters.dateTo}
              onChange={handleFilterChange}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 sm:text-sm"
            />
          </div>

          {/* Min Amount */}
          <div>
            <label htmlFor="minAmount" className="block text-sm font-medium text-gray-700 mb-1">
              Montant min. (DH)
            </label>
            <input
              type="number"
              id="minAmount"
              name="minAmount"
              value={filters.minAmount}
              onChange={handleFilterChange}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 sm:text-sm"
              placeholder="0.00"
              min="0"
              step="0.01"
            />
          </div>

          {/* Max Amount */}
          <div>
            <label htmlFor="maxAmount" className="block text-sm font-medium text-gray-700 mb-1">
              Montant max. (DH)
            </label>
            <input
              type="number"
              id="maxAmount"
              name="maxAmount"
              value={filters.maxAmount}
              onChange={handleFilterChange}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 sm:text-sm"
              placeholder="10000.00"
              min="0"
              step="0.01"
            />
          </div>

          {/* Duplicates only */}
          <div className="flex items-end pb-1">
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={showDuplicatesOnly}
                onChange={(e) => { setShowDuplicatesOnly(e.target.checked); setCurrentPage(1); }}
                className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
              />
              <span className="text-sm font-medium text-gray-700 flex items-center gap-1">
                <AlertCircle size={14} className="text-red-500" />
                Doublons uniquement
              </span>
            </label>
          </div>
        </div>

        {/* Active filter badge for duplicates */}
        {showDuplicatesOnly && (
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="text-sm text-gray-500">Filtres actifs:</span>
            <span className="inline-flex items-center px-3 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
              Doublons uniquement
              <button
                type="button"
                className="ml-1.5 inline-flex items-center justify-center h-4 w-4 rounded-full text-red-400 hover:bg-red-200 hover:text-red-500 focus:outline-none"
                onClick={() => setShowDuplicatesOnly(false)}
              >
                ×
              </button>
            </span>
          </div>
        )}
      </div>

      {/* Loading state */}
      {loading && (
        <div className="flex justify-center p-12">
          <div className="inline-flex items-center px-4 py-2 font-semibold leading-6 text-sm rounded-md text-white bg-emerald-700 transition ease-in-out duration-150">
            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Chargement des demandes...
          </div>
        </div>
      )}

      {/* Payment Requests Table */}
      {!loading && (
        <div className="bg-white rounded-lg shadow overflow-hidden border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-medium text-gray-900">
                Demandes de paiement
                {sortedRequests.length > 0 && (
                  <span className="ml-2 text-sm text-gray-500">
                    ({sortedRequests.length} résultats)
                  </span>
                )}
              </h2>

              <div className="text-sm text-gray-500">
                Page {currentPage} sur {totalPages}
              </div>
            </div>
          </div>

          {sortedRequests.length === 0 ? (
            <div className="text-center py-12">
              <CreditCard className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="mt-2 text-lg font-medium text-gray-900">Aucune demande trouvée</h3>
              <p className="mt-1 text-gray-500">
                {Object.values(filters).some(f => f) ?
                  "Aucune demande ne correspond à vos critères." :
                  "Aucune demande de paiement pour le moment."}
              </p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('dateDemande')}
                      >
                        <div className="flex items-center">
                          Date
                          {sortBy === 'dateDemande' && (
                            <span className="ml-1">
                              {sortOrder === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('user')}
                      >
                        <div className="flex items-center">
                          Demandeur
                          {sortBy === 'user' && (
                            <span className="ml-1">
                              {sortOrder === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </div>
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Organisation
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('amount')}
                      >
                        <div className="flex items-center">
                          Montant
                          {sortBy === 'amount' && (
                            <span className="ml-1">
                              {sortOrder === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('status')}
                      >
                        <div className="flex items-center">
                          Statut
                          {sortBy === 'status' && (
                            <span className="ml-1">
                              {sortOrder === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </div>
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Traitement
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {paginatedRequests.map((request) => {
                      const statusStyle = getStatusStyle(request.status);
                      const userHasDuplicates = request.hasDuplicates ?? hasDuplicateTransactions(request);

                      return (
                        <tr
                          key={request.id}
                          className={`transition-colors cursor-pointer ${userHasDuplicates ? 'bg-red-50 hover:bg-red-100' : request.transactionsCount === 0 ? 'bg-orange-50 hover:bg-orange-100' : 'hover:bg-gray-50'}`}
                          onClick={() => handleViewDetails(request)}
                        >
                          {/* Date Column */}
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  {format(new Date(request.dateDemande), 'dd MMM yyyy', { locale: fr })}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {format(new Date(request.dateDemande), 'HH:mm', { locale: fr })}
                                </div>
                              </div>
                            </div>
                          </td>

                          {/* User Column with Duplicate Warning */}
                          <td className="px-6 py-4 whitespace-nowrap">
                            {request.user ? (
                              <div className="flex items-center">
                                {/* Duplicate Transaction Warning */}
                                {userHasDuplicates && (
                                  <AlertCircle
                                    className="h-4 w-4 text-red-500 mr-2 flex-shrink-0"
                                    title="Transactions dupliquées détectées"
                                  />
                                )}

                                <div className="flex-shrink-0 h-10 w-10">
                                  <div className="h-10 w-10 rounded-full bg-gradient-to-r from-emerald-400 to-green-500 flex items-center justify-center">
                                    <span className="text-white font-medium text-sm">
                                      {(request.user.firstName?.[0] || '') + (request.user.lastName?.[0] || '')}
                                    </span>
                                  </div>
                                </div>

                                <div className="ml-4">
                                  <div className="flex items-center">
                                    <div className="text-sm font-medium text-gray-900">
                                      {request.user.firstName} {request.user.lastName}
                                    </div>
                                    {userHasDuplicates && (
                                      <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                                        ⚠ Doublons
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-sm text-gray-500">{request.user.email}</div>
                                  <div className="mt-1">
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${request.user.userType === 'PHARMACIST'
                                        ? 'bg-green-100 text-green-800'
                                        : request.user.userType === 'SELLER'
                                          ? 'bg-blue-100 text-blue-800'
                                          : request.user.userType === 'LABORATORY'
                                            ? 'bg-purple-100 text-purple-800'
                                            : 'bg-gray-100 text-gray-800'
                                      }`}>
                                      {getUserTypeLabel(request.user.userType)}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <span className="text-sm text-gray-500">Utilisateur supprimé</span>
                            )}
                          </td>

                          {/* Pharmacy/Organization Column */}
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {request.user?.pharmacy?.namePharmacy && (
                                <div className="flex items-center">
                                  <Building2 className="h-4 w-4 text-gray-400 mr-2" />
                                  <div>
                                    <div className="font-medium">{request.user.pharmacy.namePharmacy}</div>
                                    {request.user.pharmacy.region && (
                                      <div className="text-xs text-gray-500 flex items-center">
                                        <MapPin className="h-3 w-3 mr-1" />
                                        {request.user.pharmacy.region.nomRegion}
                                        {request.user.pharmacy.ville && ` - ${request.user.pharmacy.ville.nomVilleVillage}`}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                              {request.user?.ownedLaboratory && (
                                <div className="flex items-center">
                                  <Building2 className="h-4 w-4 text-gray-400 mr-2" />
                                  <div>
                                    <div className="font-medium">{request.user.ownedLaboratory.nom}</div>
                                    <div className="text-xs text-gray-500">Laboratoire</div>
                                  </div>
                                </div>
                              )}
                              {!request.user?.pharmacy && !request.user?.ownedLaboratory && (
                                <span className="text-sm text-gray-500">Aucune organisation</span>
                              )}
                            </div>
                          </td>

                          {/* Amount Column */}
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <DollarSign className="h-4 w-4 text-green-500 mr-1" />
                              <span className="text-sm font-semibold text-green-600">
                                {request.amount.toFixed(2)} DH
                              </span>
                            </div>
                          </td>

                          {/* Status Column */}
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${statusStyle.className}`}>
                              {statusStyle.icon}
                              <span className="ml-1">{statusStyle.text}</span>
                            </span>
                          </td>

                          {/* Processed At Column */}
                          <td className="px-6 py-4 whitespace-nowrap">
                            {request.processedAt ? (
                              <div className="flex items-center">
                                <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                                <div>
                                  <div className="text-sm font-medium text-gray-900">
                                    {format(new Date(request.processedAt), 'dd MMM yyyy', { locale: fr })}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {format(new Date(request.processedAt), 'HH:mm', { locale: fr })}
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <span className="text-xs text-gray-400">—</span>
                            )}
                          </td>

                          {/* Actions Column */}
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center justify-end space-x-2">
                              {request.status === 'PENDING' && (
                                <>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleApprove(request); }}
                                    className="text-green-600 hover:text-green-800 p-1 rounded hover:bg-green-50"
                                    title="Approuver"
                                  >
                                    <CheckCircle className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleReject(request); }}
                                    className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-50"
                                    title="Rejeter"
                                  >
                                    <XCircle className="h-4 w-4" />
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${currentPage === 1
                          ? 'text-gray-300 cursor-not-allowed bg-gray-100'
                          : 'text-gray-700 bg-white hover:bg-gray-50'
                        }`}
                    >
                      Précédent
                    </button>
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className={`ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${currentPage === totalPages
                          ? 'text-gray-300 cursor-not-allowed bg-gray-100'
                          : 'text-gray-700 bg-white hover:bg-gray-50'
                        }`}
                    >
                      Suivant
                    </button>
                  </div>

                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Affichage de{' '}
                        <span className="font-medium">
                          {Math.min((currentPage - 1) * pageSize + 1, sortedRequests.length)}
                        </span>{' '}
                        à{' '}
                        <span className="font-medium">
                          {Math.min(currentPage * pageSize, sortedRequests.length)}
                        </span>{' '}
                        sur{' '}
                        <span className="font-medium">{sortedRequests.length}</span> résultats
                      </p>
                    </div>

                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                        <button
                          onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                          disabled={currentPage === 1}
                          className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${currentPage === 1
                              ? 'text-gray-300 cursor-not-allowed'
                              : 'text-gray-500 hover:bg-gray-50'
                            }`}
                        >
                          <span className="sr-only">Previous</span>
                          <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </button>

                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          let pageNum;

                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = currentPage - 2 + i;
                          }

                          return (
                            <button
                              key={pageNum}
                              onClick={() => setCurrentPage(pageNum)}
                              aria-current={currentPage === pageNum ? 'page' : undefined}
                              className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${currentPage === pageNum
                                  ? 'z-10 bg-emerald-50 border-emerald-500 text-emerald-600'
                                  : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                }`}
                            >
                              {pageNum}
                            </button>
                          );
                        })}

                        <button
                          onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                          disabled={currentPage === totalPages}
                          className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${currentPage === totalPages
                              ? 'text-gray-300 cursor-not-allowed'
                              : 'text-gray-500 hover:bg-gray-50'
                            }`}
                        >
                          <span className="sr-only">Next</span>
                          <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Modals */}
      {selectedRequest && (
        <>
          {/* Payment Request Details Modal */}
          <PaymentRequestDetailsModal
            request={selectedRequest}
            isOpen={showDetailsModal}
            onClose={() => {
              setShowDetailsModal(false);
              setSelectedRequest(null);
            }}
            onRefresh={handleRefresh}
          />

          {/* Approval/Rejection Modal */}
          <ApprovalModal
            request={selectedRequest}
            isOpen={showApprovalModal}
            action={approvalAction}
            rejectionReason={rejectionReason}
            onReasonChange={setRejectionReason}
            approvalMessage={approvalMessage}
            onApprovalMessageChange={setApprovalMessage}
            onClose={() => {
              setShowApprovalModal(false);
              setSelectedRequest(null);
              setApprovalAction(null);
              setRejectionReason('');
              setApprovalMessage('');
            }}
            onConfirm={confirmAction}
          />
        </>
      )}
    </div>
  );
};

export default PaymentRequests;